import { ComponentProps, useCallback, useEffect, useRef, useState } from 'react';
import { Document, Page, PageProps, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { PdfLoadingSection } from '../../pages/payments/bill-pay-flow/components/loading/pdf-loading-section';
import { Button } from '../button/button';
import { Inline } from '../inline/inline';
import { Stack } from '../stack/stack';
import { PageControls } from './components/page-controls';
import { ZoomControls } from './components/zoom-controls';

import pdfWorkerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { cn } from '../../utils/cn';

const INITIAL_SCALE = 1;
const ZOOM_VARIATION_SCALE = 0.2;

// https://github.com/wojtekmaj/react-pdf?tab=readme-ov-file#import-worker-recommended
// https://github.com/wojtekmaj/react-pdf?tab=readme-ov-file#configure-worker
pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerSrc;

// TODO: https://github.com/wojtekmaj/react-pdf?tab=readme-ov-file#copy-worker-to-public-directory
// Should we do something when we deploy?

type CustomTextRendererArgs = Parameters<NonNullable<PageProps['customTextRenderer']>>[0];

// https://github.com/wojtekmaj/react-pdf/wiki/Recipes#highlight-text-on-the-page
function highlightPattern(
  text: CustomTextRendererArgs['str'],
  pattern: NonNullable<ComponentProps<typeof PdfViewer>['highlightedText']>,
) {
  return text.replace(pattern, (value) => `<mark>${value}</mark>`);
}

type PdfViewerProps = {
  url: string;
  fileName?: string;
  highlightedText?: string;
  showDownloadButton?: boolean;
  isHeaderVisible?: boolean;
  initialScale?: number;
  onDelete?: () => void;
  onUploadNew?: () => void;
  className?: string;
};

export const PdfViewer = ({
  url,
  fileName,
  highlightedText,
  showDownloadButton,
  isHeaderVisible = true,
  initialScale = INITIAL_SCALE,
  onDelete,
  onUploadNew,
  className,
}: PdfViewerProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [scale, setScale] = useState(initialScale);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateContainerHeight = () => {
      if (containerRef.current) {
        setContainerHeight(containerRef.current.clientHeight);
      }
    };

    updateContainerHeight();

    window.addEventListener('resize', updateContainerHeight);

    return () => window.removeEventListener('resize', updateContainerHeight);
  }, []);

  // https://github.com/wojtekmaj/react-pdf/wiki/Recipes#highlight-text-on-the-page
  const textRenderer = useCallback((textItem: CustomTextRendererArgs, textToHighlight: string) => {
    // The PDF text layer may detect an empty string
    // We want to avoid unnecessary checks in these cases to make the rendering faster
    if (!textItem.str.trim()) {
      return '';
    }

    return highlightPattern(textItem.str, textToHighlight);
  }, []);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
    setPageNumber(1);
    setIsLoaded(true);
  }

  const handleZoomIn = () => {
    setScale(scale + ZOOM_VARIATION_SCALE);
  };

  const handleZoomOut = () => {
    setScale(scale - ZOOM_VARIATION_SCALE);
  };

  function changePage(offset: number) {
    setPageNumber((prevPageNumber) => prevPageNumber + offset);
  }

  function previousPage() {
    changePage(-1);
  }

  function nextPage() {
    changePage(1);
  }

  return (
    <Stack space={4} align="stretch" className={cn('h-full bg-white', className)}>
      {isHeaderVisible && (
        <Inline justify="between">
          <p className="text-base font-medium text-black">{`Invoice ${fileName}`}</p>

          <Inline className="pr-8">
            {onUploadNew && (
              <Button variant="secondary" onClick={onUploadNew}>
                Upload New
              </Button>
            )}

            {onDelete && (
              <Button variant="destructive" onClick={onDelete}>
                Delete
              </Button>
            )}

            {showDownloadButton && (
              <Button
                variant="secondary"
                onClick={() => {
                  window.open(url, '_blank');
                }}
              >
                Download
              </Button>
            )}
          </Inline>
        </Inline>
      )}

      <div className="relative h-full overflow-clip rounded-lg border border-border">
        {isLoaded && (
          <div className="absolute top-2.5 right-5 z-10">
            <ZoomControls
              isZoomOutDisabled={scale <= INITIAL_SCALE}
              onZoomIn={handleZoomIn}
              onZoomOut={handleZoomOut}
            />
          </div>
        )}

        {isLoaded && numPages && numPages > 1 && (
          <div className="absolute right-5 bottom-2.5 z-10">
            <PageControls
              isPreviousPageDisabled={pageNumber <= 1}
              isNextPageDisabled={pageNumber >= (numPages ?? 0)}
              onPreviousPage={previousPage}
              onNextPage={nextPage}
            />
          </div>
        )}

        <div ref={containerRef} className="flex h-full w-full justify-center overflow-auto">
          <Document
            // https://github.com/wojtekmaj/react-pdf/issues/974#issuecomment-2671515611
            key={url}
            file={url}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={<PdfLoadingSection isPdfDocumentLoading />}
            className="overflow-auto"
          >
            <Page
              pageNumber={pageNumber}
              scale={scale}
              customTextRenderer={highlightedText ? (textItem) => textRenderer(textItem, highlightedText) : undefined}
              className="h-full"
              height={containerHeight}
              loading={<PdfLoadingSection />}
            />
          </Document>
        </div>
      </div>
    </Stack>
  );
};
